import { acceptLegalConsentFn } from "@/actions/legal/handler";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Link,
  useLocation,
  useRouteContext,
  useRouter,
} from "@tanstack/react-router";
import { ExternalLink, Lock, Scale, ShieldCheck, Video } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export function LegalConsentModal() {
  const { user } = useRouteContext({ from: "/_app" });
  const location = useLocation();
  const router = useRouter();
  const queryClient = useQueryClient();

  const [acceptKvkk, setAcceptKvkk] = useState(false);
  const [acceptVideo, setAcceptVideo] = useState(false);
  const [acceptHealth, setAcceptHealth] = useState(false);

  // Check if user has already accepted
  const hasAccepted = Boolean(
    user?.legalConsents?.kvkk?.accepted &&
      user?.legalConsents?.privacyPolicy?.accepted &&
      user?.legalConsents?.workoutVideoConsent?.accepted,
  );

  // Don't pop up on the privacy page itself to allow reading freely
  const isPrivacyPage = location.pathname === "/privacy";
  const shouldShow = Boolean(user && !hasAccepted && !isPrivacyPage);

  const [open, setOpen] = useState(shouldShow);

  useEffect(() => {
    setOpen(shouldShow);
  }, [shouldShow]);

  const mutation = useMutation({
    mutationFn: () =>
      acceptLegalConsentFn({
        data: {
          acceptKvkk,
          acceptPrivacyPolicy: acceptKvkk,
          acceptWorkoutVideoConsent: acceptVideo,
          acceptHealthDisclaimer: acceptHealth,
          version: "v1.0",
        },
      }),
    onSuccess: async () => {
      toast.success("Yasal metin ve video izinleri başarıyla onaylandı.");
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["legalConsentStatus"] });
      await router.invalidate();
    },
    onError: () => {
      toast.error("Onay kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.");
    },
  });

  const handleSelectAll = () => {
    const nextState = !(acceptKvkk && acceptVideo && acceptHealth);
    setAcceptKvkk(nextState);
    setAcceptVideo(nextState);
    setAcceptHealth(nextState);
  };

  const isAllSelected = acceptKvkk && acceptVideo && acceptHealth;

  if (!user || hasAccepted) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        // Prevent closing unless accepted
        if (!nextOpen && !hasAccepted) {
          return;
        }
        setOpen(nextOpen);
      }}
    >
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-xl"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="space-y-2 text-left">
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="border-primary/30 bg-primary/10 font-bold text-[11px] text-primary uppercase tracking-wider"
            >
              <ShieldCheck className="mr-1.5 h-3.5 w-3.5" />
              Zorunlu Yasal Bilgilendirme
            </Badge>
          </div>
          <DialogTitle className="font-extrabold text-foreground text-xl tracking-tight sm:text-2xl">
            KVKK, Gizlilik ve Video İzinleri Onayı
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-xs leading-relaxed sm:text-sm">
            Antrenman programınıza devam edebilmeniz, egzersiz form
            videolarınızın teknik analizi ve güvenliğiniz için lütfen aşağıdaki
            şartları inceleyip onaylayınız.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Video Highlight Card */}
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-primary/10 p-2 text-primary">
                <Video className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <h4 className="font-bold text-foreground text-xs sm:text-sm">
                  Antrenman Videolarınızın Güvenliği
                </h4>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  Yüklediğiniz tüm form videoları <strong>YALNIZCA</strong>{" "}
                  teknik koçluk değerlendirmesi amacıyla işlenir. İzniniz
                  olmadan kesinlikle sosyal medyada veya üçüncü taraflarla{" "}
                  <strong>paylaşılmaz</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Info Grid */}
          <div className="grid grid-cols-2 gap-2.5 text-xs">
            <div className="flex items-center gap-2 rounded-xl border border-border/60 bg-muted/20 p-2.5">
              <Scale className="h-4 w-4 shrink-0 text-primary" />
              <span className="text-muted-foreground">
                KVKK Madde 11 Uyumlu
              </span>
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-border/60 bg-muted/20 p-2.5">
              <Lock className="h-4 w-4 shrink-0 text-primary" />
              <span className="text-muted-foreground">
                Şifreli AWS Depolama
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between pt-1">
            <Link
              to="/privacy"
              search={{ tab: "kvkk" }}
              target="_blank"
              className="inline-flex items-center gap-1 font-medium text-primary text-xs hover:underline"
            >
              <span>Metinlerin Tamamını İncele</span>
              <ExternalLink className="h-3 w-3" />
            </Link>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleSelectAll}
              className="h-8 font-semibold text-muted-foreground text-xs hover:text-foreground"
            >
              {isAllSelected ? "Seçimi Kaldır" : "Tümünü Seç"}
            </Button>
          </div>

          <Separator />

          {/* Checkboxes */}
          <div className="space-y-3.5">
            {/* Checkbox 1: KVKK & Privacy */}
            <div
              onClick={() => setAcceptKvkk(!acceptKvkk)}
              className="flex cursor-pointer items-start gap-3 rounded-xl border border-border/60 bg-card p-3.5 transition-colors hover:bg-muted/30"
            >
              <Checkbox
                id="modal-kvkk"
                checked={acceptKvkk}
                onCheckedChange={(checked) => setAcceptKvkk(Boolean(checked))}
                className="mt-0.5"
              />
              <div className="space-y-0.5">
                <label
                  htmlFor="modal-kvkk"
                  className="cursor-pointer font-semibold text-foreground text-xs sm:text-sm"
                >
                  KVKK Aydınlatma Metni ve Gizlilik Politikası
                </label>
                <p className="text-[11px] text-muted-foreground leading-relaxed sm:text-xs">
                  Kişisel verilerimin 6698 sayılı Kanun kapsamında işlenmesine
                  ve Gizlilik Politikası hükümlerine onay veriyorum.
                </p>
              </div>
            </div>

            {/* Checkbox 2: Video Consent */}
            <div
              onClick={() => setAcceptVideo(!acceptVideo)}
              className="flex cursor-pointer items-start gap-3 rounded-xl border border-border/60 bg-card p-3.5 transition-colors hover:bg-muted/30"
            >
              <Checkbox
                id="modal-video"
                checked={acceptVideo}
                onCheckedChange={(checked) => setAcceptVideo(Boolean(checked))}
                className="mt-0.5"
              />
              <div className="space-y-0.5">
                <label
                  htmlFor="modal-video"
                  className="cursor-pointer font-semibold text-foreground text-xs sm:text-sm"
                >
                  Antrenman Form Videoları ve Açık Rıza Beyanı
                </label>
                <p className="text-[11px] text-muted-foreground leading-relaxed sm:text-xs">
                  Antrenman form videolarımın, egzersiz kayıtlarımın teknik
                  analiz ve koçluk değerlendirmesi amacıyla işlenmesine Açık
                  Rıza veriyorum.
                </p>
              </div>
            </div>

            {/* Checkbox 3: Health & Terms */}
            <div
              onClick={() => setAcceptHealth(!acceptHealth)}
              className="flex cursor-pointer items-start gap-3 rounded-xl border border-border/60 bg-card p-3.5 transition-colors hover:bg-muted/30"
            >
              <Checkbox
                id="modal-health"
                checked={acceptHealth}
                onCheckedChange={(checked) => setAcceptHealth(Boolean(checked))}
                className="mt-0.5"
              />
              <div className="space-y-0.5">
                <label
                  htmlFor="modal-health"
                  className="cursor-pointer font-semibold text-foreground text-xs sm:text-sm"
                >
                  Kullanım Koşulları ve Sağlık Beyanı
                </label>
                <p className="text-[11px] text-muted-foreground leading-relaxed sm:text-xs">
                  Ağırlık antrenmanı için sağlık durumumun uygun olduğunu ve
                  koçluk tavsiyelerinin tıbbi teşhis niteliğinde olmadığını
                  kabul ediyorum.
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 pt-2 sm:justify-end">
          <Button
            type="button"
            className="w-full py-5 font-bold text-sm shadow-md"
            disabled={!isAllSelected || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? "Onaylanıyor..." : "Onayla ve Devam Et"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
